import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Typography,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Grid,
    Card,
    CardActionArea,
    CardMedia,
    CircularProgress,
} from '@mui/material';
import { Image as ImageIcon, Delete as DeleteIcon, Upload, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';

const QuestionEditor = React.memo(({ question, onQuestionChange, allTests }) => {
    const [localQuestion, setLocalQuestion] = useState({
        question_text: '',
        answers: { answer_a: '', answer_b: '', answer_c: '', answer_d: '' },
        correct_answer: '',
        reference: '',
        solution: '',
        image: { url: '', alt_text: '' },
        category_ids: [],
    });
    const [openMediaDialog, setOpenMediaDialog] = useState(false);
    const [mediaTab, setMediaTab] = useState(0);
    const [urlInput, setUrlInput] = useState('');
    const [existingMedia, setExistingMedia] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Helper function to get full image URL
    const getImageUrl = (path) => {
        if (!path) return '';
        // If it's already a full URL, return it as is
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        // For relative paths, prepend the API URL
        return `${process.env.REACT_APP_API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    // Ensure allTests is always an array
    const memoizedAllTests = useMemo(() => {
        return Array.isArray(allTests) ? allTests : [];
    }, [allTests]);

    // Get selected tests based on category_ids
    const selectedTests = useMemo(() => {
        return memoizedAllTests.filter((test) => localQuestion.category_ids.includes(test._id));
    }, [memoizedAllTests, localQuestion.category_ids]);

    useEffect(() => {
        if (question && JSON.stringify(question) !== JSON.stringify(localQuestion)) {
            setLocalQuestion((prevState) => ({
                ...prevState,
                ...question,
                answers: {
                    answer_a: '',
                    answer_b: '',
                    answer_c: '',
                    answer_d: '',
                    ...(question.answers || {}),
                },
                category_ids: Array.isArray(question.category_ids) ? question.category_ids : [],
                image: question.image || { url: '', alt_text: '' },
            }));
        }
    }, [question]);

    const handleChange = useCallback((field, value) => {
        setLocalQuestion((prevState) => {
            const updatedQuestion = { ...prevState };
            if (field.startsWith('answers.')) {
                const answerKey = field.split('.')[1];
                updatedQuestion.answers = { ...updatedQuestion.answers, [answerKey]: value };
            } else if (field === 'category_ids') {
                // Ensure we're extracting _id correctly from the test objects
                updatedQuestion.category_ids = value.map((test) => test._id);
            } else if (field === 'image') {
                updatedQuestion.image = { ...updatedQuestion.image, ...value };
            } else {
                updatedQuestion[field] = value;
            }
            return updatedQuestion;
        });
    }, []);

    useEffect(() => {
        // Only trigger the callback if we have actual content
        if (localQuestion.question_text || Object.values(localQuestion.answers).some((answer) => answer)) {
            const timer = setTimeout(() => {
                console.log('Sending updated question:', localQuestion); // Debug log
                onQuestionChange(localQuestion);
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [localQuestion, onQuestionChange]);

    // Handle file upload
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        try {
            setLoading(true);
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/media/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            handleChange('image', {
                url: response.data.url,
                alt_text: file.name,
            });
            setOpenMediaDialog(false);
        } catch (err) {
            setError('Failed to upload image');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle URL submission
    const handleUrlSubmit = async () => {
        if (!urlInput) {
            setError('Please enter a URL');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/media/url`,
                { url: urlInput },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                },
            );

            handleChange('image', {
                url: response.data.url,
                alt_text: 'URL Image',
            });
            setOpenMediaDialog(false);
        } catch (err) {
            setError('Invalid image URL');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch existing media
    const fetchExistingMedia = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/media`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });
            setExistingMedia(response.data.media);
        } catch (err) {
            setError('Failed to load media library');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (openMediaDialog && mediaTab === 2) {
            fetchExistingMedia();
        }
    }, [openMediaDialog, mediaTab]);

    const handleRemoveImage = () => {
        handleChange('image', { url: '', alt_text: '' });
    };

    const handleMediaSelect = (media) => {
        handleChange('image', {
            url: media.url,
            alt_text: media.alt_text,
        });
        setOpenMediaDialog(false);
    };

    return (
        <Box sx={{ mt: 2, ml: 2, pr: 2 }}>
            <TextField
                fullWidth
                label="Question"
                value={localQuestion.question_text}
                onChange={(e) => handleChange('question_text', e.target.value)}
                margin="normal"
                multiline
                inputProps={{
                    style: { 
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word'
                    }
                }}
            />
            {['A', 'B', 'C', 'D'].map((option) => (
                <TextField
                    key={option}
                    fullWidth
                    label={`Answer ${option}`}
                    value={localQuestion.answers[`answer_${option.toLowerCase()}`]}
                    onChange={(e) => handleChange(`answers.answer_${option.toLowerCase()}`, e.target.value)}
                    margin="normal"
                    multiline
                    inputProps={{
                        style: { 
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word'
                        }
                    }}
                />
            ))}
            <FormControl fullWidth margin="normal">
                <InputLabel>Correct Answer</InputLabel>
                <Select value={localQuestion.correct_answer || ''} onChange={(e) => handleChange('correct_answer', e.target.value)}>
                    {['A', 'B', 'C', 'D'].map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                fullWidth
                label="Reference"
                value={localQuestion.reference || ''}
                onChange={(e) => handleChange('reference', e.target.value)}
                margin="normal"
                multiline
                inputProps={{
                    style: { 
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word'
                    }
                }}
            />
            <TextField
                fullWidth
                label="Solution"
                value={localQuestion.solution || ''}
                onChange={(e) => handleChange('solution', e.target.value)}
                margin="normal"
                multiline
                rows={3}
                inputProps={{
                    style: { 
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word'
                    }
                }}
            />
            <Box sx={{ mt: 2 }}>
                <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    onChange={(e) => onImageUpload(e, localQuestion._id)}
                />
            </Box>
            <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={() => setOpenMediaDialog(true)} startIcon={<ImageIcon />}>
                    {localQuestion.image?.url ? 'Change Image' : 'Add Image'}
                </Button>

                {localQuestion.image?.url && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img
                            src={getImageUrl(localQuestion.image.url)}
                            alt={localQuestion.image.alt_text}
                            style={{ maxWidth: '100%', maxHeight: '200px' }}
                        />
                        <Button variant="contained" color="error" onClick={handleRemoveImage} startIcon={<DeleteIcon />} sx={{ mt: 1 }}>
                            Remove Image
                        </Button>
                    </Box>
                )}
            </Box>
            {/* Media Selection Dialog */}
            <Dialog 
                open={openMediaDialog} 
                onClose={() => setOpenMediaDialog(false)} 
                maxWidth="md" 
                fullWidth
                disableScrollLock
            >
                <DialogTitle>Select Media</DialogTitle>
                <DialogContent>
                    <Tabs value={mediaTab} onChange={(e, newValue) => setMediaTab(newValue)} centered sx={{ mb: 2 }}>
                        <Tab icon={<Upload />} label="Upload" />
                        <Tab icon={<LinkIcon />} label="URL" />
                        <Tab icon={<ImageIcon />} label="Media Library" />
                    </Tabs>

                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    {mediaTab === 0 && (
                        <Box sx={{ textAlign: 'center' }}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="media-file-upload"
                                type="file"
                                onChange={handleFileUpload}
                            />
                            <label htmlFor="media-file-upload">
                                <Button variant="contained" component="span" disabled={loading}>
                                    {loading ? <CircularProgress size={24} /> : 'Choose File'}
                                </Button>
                            </label>
                        </Box>
                    )}

                    {mediaTab === 1 && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Image URL"
                                value={urlInput}
                                onChange={(e) => setUrlInput(e.target.value)}
                                error={!!error}
                            />
                            <Button variant="contained" onClick={handleUrlSubmit} disabled={loading || !urlInput}>
                                {loading ? <CircularProgress size={24} /> : 'Add URL'}
                            </Button>
                        </Box>
                    )}

                    {mediaTab === 2 && (
                        <Grid container spacing={2} sx={{ height: 'auto', minHeight: '200px' }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 2 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                existingMedia.map((media) => (
                                    <Grid item xs={12} sm={6} md={4} key={media._id || media.url}>
                                        <Card>
                                            <CardActionArea onClick={() => handleMediaSelect(media)}>
                                                <CardMedia
                                                    component="img"
                                                    height="140"
                                                    image={getImageUrl(media.url)}
                                                    alt={media.alt_text || 'Media image'}
                                                    loading="lazy"
                                                />
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                ))
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenMediaDialog(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>
            <Autocomplete
                multiple
                options={memoizedAllTests}
                getOptionLabel={(option) => option?.name || ''}
                value={selectedTests}
                onChange={(event, newValue) => handleChange('category_ids', newValue)}
                isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option._id === value._id;
                }}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Assign to Tests" placeholder="Search tests" />}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => <Chip label={option?.name || ''} {...getTagProps({ index })} key={option?._id || index} />)
                }
                sx={{ mt: 2, mb: 2 }}
            />
        </Box>
    );
});

export default QuestionEditor;
